import styled from '@emotion/styled'
import {
  MdSkipNext as Next,
  MdPause as Pause,
  MdPlayArrow as Play,
  MdSkipPrevious as Previous,
} from 'react-icons/md'
import { useSelector } from 'react-redux'
import Icon from '../Icon'

import { fmtToMSS } from '../../lib/player'

import BandcampLink from '../BandcampLink'
import Scrubber from './Scrubber'
import Volume from './Volume'

const Center = styled.div`
  margin-right: 0;
  align-items: center;
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.media.mUp} {
    flex: 0 0 190px;
    margin-right: ${({ theme }) => theme.space[3]};
  }

  ${({ theme }) => theme.media.mUp} {
    flex: 0 0 250px;
  }
`

const CenterLower = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
  width: 100%;

  ${({ theme }) => theme.media.mUp} {
    justify-content: space-between;
  }
`

const Controls = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto;

  div,
  svg {
    height: 46px;
    width: 46px;
  }

  ${({ theme }) => theme.media.mUp} {
    div,
    svg {
      height: 32px;
      width: 32px;
    }
  }
`

const Cover = styled.img`
  display: block;
  flex: 0 0 50px;
  margin-right: ${({ theme }) => theme.space[1]};
  width: 50px;
`

const Details = styled.div`
  font-size: 14px;
  margin-right: ${({ theme }) => theme.space[2]};
  overflow: hidden;

  div {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`

const Info = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  margin-bottom: ${({ theme }) => theme.space[1]};

  ${({ theme }) => theme.media.mUp} {
    margin-bottom: 0;
  }
`

const MobileBandcampLink = styled(BandcampLink)`
  display: block;
  margin-bottom: -4px;
  position: absolute;
  right: 0;
`

const StyledScrubber = styled(Scrubber)`
  margin-bottom: ${({ theme }) => theme.space[0]};
  width: 100%;
`

const StyledVolume = styled(Volume)`
  float: left;
  width: 60%;

  ${({ theme }) => theme.media.mUp} {
    width: 100%;
  }
`

const Time = styled.span`
  flex: 0 0 56px;
  font-size: 11px;
  position: absolute;
  left: 0;
`

const VolumeContainer = styled.div`
  align-self: center;
  display: none;
  flex: 0 0 80px;
  margin-top: ${({ theme }) => theme.space[3]};

  ${({ theme }) => theme.media.lUp} {
    display: block;
  }
`

const PlayerContents = ({
  close,
  duration,
  next,
  onScrubEnd,
  pause,
  play,
  previous,
  progress,
  scrub,
}) => {
  const { audioLoading, playIndex, playing, playlist } = useSelector(
    (state) => state.player
  )

  const hasNext = playIndex < playlist.length - 1
  const hasPrevious = playIndex > 0

  const currentTrack = playlist.length > 0 ? playlist[playIndex] : {}
  const { artist, cover, listId, releaseTitle, title, url } = currentTrack

  const getTime = (progress) => `${!!progress ? fmtToMSS(progress) : '0:00'}`
  const getDuration = (duration) =>
    `${!!duration ? fmtToMSS(duration) : '0:00'}`

  return (
    <>
      <Info>
        <Cover id="track-art" src={cover} />
        <Details>
          <div className="b mb1" id="track-title">
            {title}
          </div>
          <div>
            by <span id="track-artist">{artist}</span>
          </div>
          <div className="dn" id="track-album">
            {releaseTitle}
          </div>
        </Details>
      </Info>
      <Center>
        <StyledScrubber
          duration={duration}
          onScrub={scrub}
          onScrubEnd={onScrubEnd}
          progress={progress}
        />
        <CenterLower>
          <Time>
            {audioLoading ? (
              'loading...'
            ) : (
              <div>
                <span id="time">{getTime(progress)}</span>
                <span>/</span>
                <span id="duration">{getDuration(duration)}</span>
              </div>
            )}
          </Time>
          <Controls>
            <Icon inactive={!hasPrevious}>
              <Previous
                className="mr1-ns"
                onClick={previous}
                title="previous"
              />
            </Icon>

            {playing ? (
              <Icon>
                <Pause
                  className="mr1-ns"
                  id="pause-button"
                  onClick={pause}
                  title="pause"
                />
              </Icon>
            ) : (
              <Icon>
                <Play
                  className="mr1-ns"
                  id="play-button"
                  onClick={play}
                  title="play"
                />
              </Icon>
            )}
            <Icon inactive={!hasNext}>
              <Next onClick={next} title="next" />
            </Icon>
          </Controls>
          <MobileBandcampLink listId={listId} url={url} />
        </CenterLower>
      </Center>
      <VolumeContainer>
        <StyledVolume />
      </VolumeContainer>
    </>
  )
}

export default PlayerContents
